import { Component, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { ToastrService } from "ngx-toastr";
import { ImageMasterService } from "src/app/Common/ImageMasterService/image-master.service";
import { NotificationMasterCreateRequestDTO } from "src/app/Common/NotificationMaster/notificationMaster.interface";
import { NotificationMasterService } from "src/app/Common/NotificationMaster/notificationMaster.service";
import {
  SchoolDocMasterCreateBulkObjectDTO,
  SchoolDocMasterCreateBulkRequestDTO,
  SchoolDocMasterReadByMasterIdRequestDTO,
} from "src/app/School/BackOffice/school-document/school-doc-master.interface";
import { SchoolDocMasterService } from "src/app/School/BackOffice/school-document/school-doc-master.service";
import { SchoolMasterDTO, SchoolMasterReadByIdRequestDTO } from "src/app/School/BackOffice/school-master/school-master.interface";
import { SchoolMasterService } from "src/app/School/BackOffice/school-master/school-master.service";
import {
  DocumentVerificationSessionDeleteRequestDTO,
  DocumentVerificationSessionDTO,
  DocumentVerificationSessionReadByIdRequestDTO,
} from "src/app/Shared Utilities/documentVerificationSession/documentVerificationSession.interface";
import { DocumentVerificationSessionService } from "src/app/Shared Utilities/documentVerificationSession/documentVerificationSession.service";
import { environment } from "src/environment/environment";

@Component({
  selector: "app-school-doc-verification",
  templateUrl: "./school-doc-verification.component.html",
  styleUrls: ["./school-doc-verification.component.css"],
})
export class SchoolDocVerificationComponent {
  private imgUploadService = inject(ImageMasterService);
  private documentVerificationSessionServices = inject(
    DocumentVerificationSessionService
  );
  private schoolDocMasterService = inject(SchoolDocMasterService);
  private schoolMasterService = inject(SchoolMasterService);
  private toastr = inject(ToastrService);

  // Timer
  timerText: string = "00:00:00";
  totalSeconds: number = 0;
  timerInterval: any = null;

  isAllRequiredDocumentsUploaded: boolean = false;
  sessionInfo: DocumentVerificationSessionDTO | null = null;

  isDataLoading: boolean = true;

  private notificationMasterService = inject(NotificationMasterService);

  isUploading: boolean = false;
  showThankYouTemplate: boolean = false;

  verificationDocuments: { [key: string]: string } = {};

  schoolInfo: SchoolMasterDTO | null = null;

  private readonly allowedFileExtensions = ['pdf', 'jpg', 'jpeg', 'png'];
  private readonly allowedMimeTypes = ['application/pdf', 'image/jpeg', 'image/png'];
  private readonly maxFileSizeInBytes = 10 * 1024 * 1024; // 10 MB

  // Documents
  documents = [
    {
      name: "School-Registration-Certificate",
      displayName: "School Registration Certificate",
      category: "School Onboarding Verification",
      subtext: "Verify registration certificate and validity",
      required: true,
      uploaded: false,
      uploading: false,
      fileName: "",
      fileUrl: "",
      isAlreadyUploaded: false,
      uploadedTime: "",
    },
    {
      name: "Trust/Society/Company-Registration-Certificate",
      displayName: "Trust/Society/Company Registration Certificate",
      category: "School Onboarding Verification",
      subtext: "Verify school entity registration",
      required: true,
      uploaded: false,
      uploading: false,
      fileName: "",
      fileUrl: "",
      isAlreadyUploaded: false,
      uploadedTime: "",
    },
    {
      name: "UDISE+-Number-Certificate",
      displayName: "UDISE+ Number Certificate",
      category: "School Onboarding Verification",
      subtext: "Verify UDISE+ number and school details",
      required: true,
      uploaded: false,
      uploading: false,
      fileName: "",
      fileUrl: "",
      isAlreadyUploaded: false,
      uploadedTime: "",
    },
    {
      name: "Board-Affiliation-Certificate",
      displayName: "Board Affiliation Certificate",
      category: "School Onboarding Verification",
      subtext: "Verify affiliation with CBSE/ICSE/State Board",
      required: true,
      uploaded: false,
      uploading: false,
      fileName: "",
      fileUrl: "",
      isAlreadyUploaded: false,
      uploadedTime: "",
    },
    {
      name: "Recognition-Letter",
      displayName: "Recognition Letter",
      category: "School Onboarding Verification",
      subtext: "Verify recognition from State Education Department",
      required: true,
      uploaded: false,
      uploading: false,
      fileName: "",
      fileUrl: "",
      isAlreadyUploaded: false,
      uploadedTime: "",
    },
    {
      name: "Fire-NOC-/-Building-Fitness-Certificate",
      displayName: "Fire NOC / Building Fitness Certificate",
      category: "School Onboarding Verification",
      subtext: "Verify fire safety and building fitness validity",
      required: true,
      uploaded: false,
      uploading: false,
      fileName: "",
      fileUrl: "",
      isAlreadyUploaded: false,
      uploadedTime: "",
    },
    {
      name: "Land-Ownership-/-Lease-Documents",
      displayName: "Land Ownership / Lease Documents",
      category: "School Onboarding Verification",
      subtext: "Verify land title or lease validity",
      required: true,
      uploaded: false,
      uploading: false,
      fileName: "",
      fileUrl: "",
      isAlreadyUploaded: false,
      uploadedTime: "",
    },
    {
      name: "Financial-Statements-/-ITR",
      displayName: "Financial Statements / ITR",
      category: "School Onboarding Verification",
      subtext: "Verify audited financials and tax returns",
      required: true,
      uploaded: false,
      uploading: false,
      fileName: "",
      fileUrl: "",
      isAlreadyUploaded: false,
      uploadedTime: "",
    },
    {
      name: "KYC-of-School-Authority",
      displayName: "KYC of School Authority",
      category: "School Onboarding Verification",
      subtext: "Verify identity of authorized school authority",
      required: true,
      uploaded: false,
      uploading: false,
      fileName: "",
      fileUrl: "",
      isAlreadyUploaded: false,
      uploadedTime: "",
    },
  ];

  uploadedCount = 0;
  totalDocuments = 0;
  uploadPercent = 0;

  constructor(private route: ActivatedRoute, private router: Router) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((param) => {
      const sessionId = param.get("sessionId");
      const schoolId = param.get("schoolId");

      if (sessionId && /^[0-9]+$/.test(sessionId)) {
        this.verifySessionAndGetData(Number(sessionId));
        this.getSchoolInfoBySchoolId(Number(schoolId));
        this.getDocumentsByMasterId(
          "School Onboarding Verification",
          Number(schoolId)
        );
      } else {
        this.isDataLoading = false;
      }
    });
  }

  getSchoolInfoBySchoolId(schoolId: number) {
      const payload: SchoolMasterReadByIdRequestDTO = {
        schoolId,
      };
  
      this.schoolMasterService
        .SchoolMaster_ExternalReadById(payload)
        .subscribe({
          next: (res) => {
            this.schoolInfo = res;
          },
          error: (err) => {
            console.log(err);
          },
        });
    }

  verifySessionAndGetData(sessionId: number) {
    const payload: DocumentVerificationSessionReadByIdRequestDTO = {
      sessionId: sessionId,
    };

    this.documentVerificationSessionServices
      .DocumentVerificationSession_ExternalReadById(payload)
      .subscribe({
        next: (res) => {
          if (res && res.masterType === "School Onboarding") {
            const expiryTime = new Date(res.expiresOn).getTime();
            const currentTime = new Date().getTime();

            if (expiryTime > currentTime) {
              this.sessionInfo = res;
              this.calculateRemainingTime(res.expiresOn.toString());
              this.startTimer();
            } else {
              this.sessionInfo = null;
              this.stopTimer();
              this.timerText = "00:00:00";
            }
          } else {
            this.sessionInfo = null;
            this.stopTimer();
          }

          this.isDataLoading = false;
        },
        error: (err) => {
          this.sessionInfo = null;
          this.stopTimer();
          this.isDataLoading = false;
        },
      });
  }

  calculateRemainingTime(expiresOn: string) {
    const expiry = new Date(expiresOn).getTime();
    const now = new Date().getTime();

    let diff = Math.floor((expiry - now) / 1000);

    if (diff <= 0) {
      this.timerText = "00:00:00";
      this.totalSeconds = 0;
      return { timerText: "00:00:00", totalSeconds: 0 };
    }

    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;

    const timerText =
      (hours < 10 ? "0" + hours : hours) +
      ":" +
      (minutes < 10 ? "0" + minutes : minutes) +
      ":" +
      (seconds < 10 ? "0" + seconds : seconds);

    this.timerText = timerText;
    this.totalSeconds = diff;

    return true;
  }

  // Timer countdown
  startTimer() {
    this.stopTimer();

    this.timerInterval = setInterval(() => {
      if (this.totalSeconds <= 0) {
        this.stopTimer();
        this.sessionInfo = null;
        this.timerText = "00:00:00";
        return;
      }

      const hours = Math.floor(this.totalSeconds / 3600);
      const minutes = Math.floor((this.totalSeconds % 3600) / 60);
      const seconds = this.totalSeconds % 60;

      this.timerText = `${this.pad(hours)}:${this.pad(minutes)}:${this.pad(
        seconds
      )}`;
      this.totalSeconds--;
    }, 1000);
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  pad(num: number): string {
    return num.toString().padStart(2, "0");
  }

  // isAllDocumentUploaded() {


  //   let isValid = true;

  //   for (const d of this.documents) {
  //     if (
  //       d.required &&
  //       !d.isAlreadyUploaded &&
  //       !d.fileUrl
  //     ) {
  //       isValid = false;
  //       break;
  //     }
  //   }

  //   this.isAllRequiredDocumentsUploaded = isValid;
  // }

  // Upload document with loader
  
  isAllDocumentUploaded() {
  const hasAtLeastOneNewDocumentUploaded = this.documents.some(
    (d) => !!d.fileUrl && d.uploaded
  );

  this.isAllRequiredDocumentsUploaded = hasAtLeastOneNewDocumentUploaded;
}

private isValidDocumentFile(file: File): boolean {
  const fileName = file.name || "";
  const fileExtension = fileName.split(".").pop()?.toLowerCase() || "";

  if (!this.allowedFileExtensions.includes(fileExtension)) {
   
    this.toastr.error(
      "Invalid file type. Only PDF, JPG, JPEG, and PNG files are allowed."
    );
    return false;
  }

  if (!this.allowedMimeTypes.includes(file.type)) {
    this.toastr.error(
      "Invalid file format. Please upload only PDF, JPG, JPEG, or PNG."
    );
    return false;
  }

  if (file.size > this.maxFileSizeInBytes) {
    this.toastr.error('File size should not exceed 10 MB.');
    return false;
  }

  return true;
}
  
  // uploadDocument(index: number, event: any) {
  //   const file = event.target.files[0];
  //   if (!file) return;

  //   this.uploadDocumentAndStoreURL(file, index);
  // }

  uploadDocument(index: number, event: any) {
  const file = event.target.files[0];

  if (!file) return;

  if (!this.isValidDocumentFile(file)) {
    event.target.value = '';
    return;
  }

  this.uploadDocumentAndStoreURL(file, index);
}

  removeDocument(index: number) {
  this.documents[index].uploaded = false;
  this.documents[index].fileName = "";
  this.documents[index].uploadedTime = "";
  this.documents[index].uploading = false;
  this.documents[index].fileUrl = "";

  this.updateProgress();
}

  // removeDocument(index: number) {
  //   this.documents[index].uploaded = false;
  //   this.documents[index].fileName = "";
  //   this.documents[index].uploadedTime = "";
  //   this.documents[index].uploading = false;
  //   this.updateProgress();
  //   this.isAllDocumentUploaded();
  // }

  // updateProgress() {
  //   this.uploadedCount = this.documents.filter((d) => d.uploaded).length;
  //   this.uploadPercent = (this.uploadedCount / this.totalDocuments) * 100;
  // }

  updateProgress() {
  this.uploadedCount = this.documents.filter((d) => d.uploaded).length;

  const availableDocuments = this.documents.filter((d) => !d.isAlreadyUploaded).length;
  this.totalDocuments = availableDocuments > 0 ? availableDocuments : this.documents.length;

  this.uploadPercent =
    this.totalDocuments > 0
      ? Math.round((this.uploadedCount / this.totalDocuments) * 100)
      : 0;

  this.isAllDocumentUploaded();
}

  uploadDocumentAndStoreURL(file: File, index: number): Promise<string> {
    const doc = this.documents[index];
    doc.uploading = true; // start loader

    return new Promise((resolve, reject) => {
      this.imgUploadService.ImageMaster_ExternalUpload(file).subscribe({
        next: (res) => {
          const imgURL = res.fileURL;

          doc.uploading = false; // stop loader
          doc.uploaded = true;
          doc.fileName = file.name;
          const now = new Date();
          doc.uploadedTime =
            now.toLocaleDateString() + " " + now.toLocaleTimeString();
          doc.fileUrl = imgURL;
          this.updateProgress();
          this.isAllDocumentUploaded();
          resolve(imgURL);
        },
        error: (err) => {
          console.log(err);
          reject(err);
        },
      });
    });
  }

  // bulkUploadAllTheDocuments() {
  //   if (!this.sessionInfo) {
  //     alert("Session not exist.");
  //     return;
  //   } else {
  //     this.isUploading = true;

  //     const data: DocumentVerificationSessionReadByIdRequestDTO = {
  //       sessionId: this.sessionInfo.sessionId,
  //     };
  //     this.documentVerificationSessionServices
  //       .DocumentVerificationSession_ExternalReadById(data)
  //       .subscribe({
  //         next: (res) => {
  //           const allDocument = this.documents.filter((d) => d.fileUrl !== "");

  //           const docList: SchoolDocMasterCreateBulkObjectDTO[] =
  //             allDocument.map<SchoolDocMasterCreateBulkObjectDTO>((d) => {
  //               return {
  //                 masterType: d.category,
  //                 masterId: this.sessionInfo ? this.sessionInfo.masterId : 0,
  //                 docType: d.category,
  //                 docNo: d.name,
  //                 docDescription: d.subtext,
  //                 docPath: d.fileUrl,
  //               };
  //             });

  //           const payload: SchoolDocMasterCreateBulkRequestDTO = {
  //             docList: docList,
  //             actionUser: "Not Login",
  //           };

  //           this.schoolDocMasterService
  //             .SchoolDocMaster_ExternalCreateBulk(payload)
  //             .subscribe({
  //               next: (res) => {
  //                 this.openSuccessModal();
  //                 this.deleteSession();
  //                 this.isUploading = false;
  //               },
  //               error: (err) => {
  //                 console.log(err);
  //                 this.isUploading = false;
  //               },
  //             });
  //           this.isDataLoading = false;
  //         },
  //         error: (err) => {
  //           console.log(err);
  //           this.refreshPage();
  //           this.isDataLoading = false;
  //         },
  //       });
  //   }
  // }

  private getExternalActionUser(): string {
  const sessionId = this.sessionInfo?.sessionId || 0;
  const schoolId = this.sessionInfo?.masterId || 0;

  return `ExternalSchoolDocUpload_Session_${sessionId}_School_${schoolId}`;
}

  bulkUploadAllTheDocuments() {
  if (!this.sessionInfo) {
    this.toastr.error("Session not exist.");
    return;
  }

  const allDocument = this.documents.filter((d) => d.fileUrl !== "");

  if (allDocument.length === 0) {
    this.toastr.warning("Please upload at least one document before submitting.");
    return;
  }

  this.isUploading = true;

  const data: DocumentVerificationSessionReadByIdRequestDTO = {
    sessionId: this.sessionInfo.sessionId,
  };

  this.documentVerificationSessionServices
    .DocumentVerificationSession_ExternalReadById(data)
    .subscribe({
      next: (res) => {
        const docList: SchoolDocMasterCreateBulkObjectDTO[] =
          allDocument.map<SchoolDocMasterCreateBulkObjectDTO>((d) => {
            return {
              masterType: d.category,
              masterId: this.sessionInfo ? this.sessionInfo.masterId : 0,
              docType: d.category,
              docNo: d.name,
              docDescription: d.subtext,
              docPath: d.fileUrl,
            };
          });

        const payload: SchoolDocMasterCreateBulkRequestDTO = {
          docList: docList,
          actionUser: this.getExternalActionUser(),
        };

        this.schoolDocMasterService
          .SchoolDocMaster_ExternalCreateBulk(payload)
          .subscribe({
            next: (res) => {
              this.sendSubmittedDocumentsNotification(allDocument);
              this.openSuccessModal();
              this.deleteSession();
              this.isUploading = false;
            },
            error: (err) => {
              console.log(err);
              this.toastr.error("Failed to submit documents.");
              this.isUploading = false;
            },
          });
      },
      error: (err) => {
        console.log(err);
        this.toastr.error("Session verification failed.");
        this.isUploading = false;
      },
    });
}

  deleteSession() {
    if (this.sessionInfo) {
      // const payload: DocumentVerificationSessionDeleteRequestDTO = {
      //   sessionId: this.sessionInfo.sessionId,
      //   actionUser: "Not Login",
      // };

      const payload: DocumentVerificationSessionDeleteRequestDTO = {
  sessionId: this.sessionInfo.sessionId,
  actionUser: this.getExternalActionUser(),
};

      this.documentVerificationSessionServices
        .DocumentVerificationSession_ExternalDelete(payload)
        .subscribe({
          next: (res) => {},
          error: (err) => {
            console.log(err);
          },
        });
    }
  }

  modalEl: any;
  modalInstance: any;

  ngAfterViewInit(): void {
    this.modalEl = document.getElementById("successPopper");

    // Use Bootstrap JS available in window scope
    // Avoids TypeScript import errors completely
    // @ts-ignore
    this.modalInstance = new window.bootstrap.Modal(this.modalEl, {
      backdrop: "static",
      keyboard: false,
    });
  }

  openSuccessModal() {
    if (this.modalInstance) {
      this.modalInstance.show();
    }
  }

  closeSuccessModal() {
    if (this.modalInstance) {
      this.modalInstance.hide();
    }
  }

  showThankYouPage() {
    this.closeSuccessModal();
    this.stopTimer();
    this.showThankYouTemplate = true;
    this.sessionInfo = null;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  refreshPage() {
    window.location.reload();
  }

  isDocumentUploadedPresent(docType: string = "") {
    return Object.keys(this.verificationDocuments).includes(docType);
  }

  getDocumentsByMasterId(masterType: string, masterId: number) {
      const payload: SchoolDocMasterReadByMasterIdRequestDTO = {
        masterType,
        masterId,
      };
  
      this.schoolDocMasterService
        .SchoolDocMaster_ExternalReadByMasterId(payload)
        .subscribe({
          next: (res) => {
            res.items.forEach((d) => {
              this.verificationDocuments[d.docNo] = d.docPath;
            });
  
            this.documents.map((d) => {
              d.isAlreadyUploaded = this.isDocumentUploadedPresent(d.name);
            });
  
            this.documents.map((d) => {
  d.isAlreadyUploaded = this.isDocumentUploadedPresent(d.name);
});

this.totalDocuments = this.documents.filter((d) => !d.isAlreadyUploaded).length;
this.updateProgress();

          },
          error: (err) => {
            console.log(err);
          },
        });
    }


  ngOnDestroy(): void {
    this.stopTimer();
  }



// =========== Mail notification ==============

private getDocumentFullUrl(path: string): string {
  const cleanPath = (path || "").trim();

  if (!cleanPath) return "";

  if (cleanPath.startsWith("http://") || cleanPath.startsWith("https://")) {
    return encodeURI(cleanPath);
  }

  const relativePath = cleanPath.startsWith("/") ? cleanPath.substring(1) : cleanPath;

  if (relativePath.toLowerCase().startsWith("uploads/")) {
    return encodeURI(`https://dev.apig.nishwik.com/${relativePath}`);
  }

  return encodeURI(`https://dev.apig.nishwik.com/Uploads/${relativePath}`);
}

private getSubmittedDocumentEmailTemplate(
  uploadedDocuments: any[],
  sessionData: DocumentVerificationSessionDTO
): string {
  const documentRows = uploadedDocuments
    .map((doc, index) => {
      const documentUrl = this.getDocumentFullUrl(doc.fileUrl);

      return `
        <tr>
          <td style="padding:10px;border-bottom:1px solid #eee;">${index + 1}</td>
          <td style="padding:10px;border-bottom:1px solid #eee;font-weight:600;">${doc.displayName}</td>
          <td style="padding:10px;border-bottom:1px solid #eee;">${doc.fileName || "-"}</td>
          <td style="padding:10px;border-bottom:1px solid #eee;">
            <a href="${documentUrl}" target="_blank" style="color:#011044;font-weight:700;text-decoration:underline;">View Document</a>
          </td>
        </tr>`;
    })
    .join("");

  return `
<!DOCTYPE html>
<html>
<body style="font-family:Segoe UI,Arial,sans-serif;background:#f4f7ff;margin:0;padding:24px;">
  <div style="max-width:760px;margin:auto;background:#ffffff;border-radius:14px;overflow:hidden;box-shadow:0 6px 18px rgba(0,0,0,0.08);">
    <div style="background:#011044;color:#fff;padding:22px 28px;">
      <h2 style="margin:0;">School Documents Submitted</h2>
      <p style="margin:6px 0 0;">A school has submitted documents for verification.</p>
    </div>

    <div style="padding:28px;">
      <h3 style="margin-top:0;color:#011044;">School Details</h3>
      <p><b>School Name:</b> ${this.schoolInfo?.schoolName || "-"}</p>
      <p><b>School ID:</b> ${sessionData.masterId}</p>
      <p><b>Email:</b> ${this.schoolInfo?.email || "-"}</p>
      <p><b>Phone:</b> ${this.schoolInfo?.phoneNo || "-"}</p>
      <p><b>Session ID:</b> ${sessionData.sessionId}</p>

      <h3 style="color:#011044;margin-top:24px;">Uploaded Document List</h3>

      <table style="width:100%;border-collapse:collapse;border:1px solid #eee;">
        <thead>
          <tr style="background:#f4f7ff;">
            <th style="text-align:left;padding:10px;">#</th>
            <th style="text-align:left;padding:10px;">Document</th>
            <th style="text-align:left;padding:10px;">File Name</th>
            <th style="text-align:left;padding:10px;">Link</th>
          </tr>
        </thead>
        <tbody>
          ${documentRows}
        </tbody>
      </table>

      <p style="margin-top:22px;">Please check these documents in <b>School Document Verification</b>.</p>
    </div>
  </div>
</body>
</html>`;
}

private sendSubmittedDocumentsNotification(uploadedDocuments: any[]) {
  if (!this.sessionInfo) return;

  const data: NotificationMasterCreateRequestDTO = {
    alertConfigId: environment.alertConfigId,
    nType: "Email",
    nSubject: `School documents submitted for verification - ${this.schoolInfo?.schoolName || "School"}`,
    nContent: this.getSubmittedDocumentEmailTemplate(uploadedDocuments, this.sessionInfo),
    nTo: environment.production === true ? "vinay.pandey@nishwikbank.com" : "globalnishwikdev@gmail.com",
    nCc: "",
    nBcc: "",
    nStatus: "Pending",
    remarks: "",
    attachmentPath: "",
    scheduleddate: new Date(),
    retryCount: 1,
    retryDate: new Date(),
    postSendDataSourceType: "",
    postSendDataSourceDef: "",
    dbConnId: 0,
    actionUser: this.getExternalActionUser(),
  };

  this.notificationMasterService.NotificationMaster_ExternalCreate(data).subscribe({
    next: () => {},
    error: (err) => {
      console.log("Submit document email failed", err);
    },
  });
}






}
